'use client';

import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { GuitarStatus } from './mockData';
import Badge from '@/components/ui/Badge';

const statusOptions: { value: GuitarStatus; label: string; variant: 'active' | 'on-loan' | 'for-sale' | 'archived' }[] = [
  { value: 'active', label: 'Active', variant: 'active' },
  { value: 'on-loan', label: 'On Loan', variant: 'on-loan' },
  { value: 'for-sale', label: 'For Sale', variant: 'for-sale' },
  { value: 'archived', label: 'Archived', variant: 'archived' },
];

interface StatusDropdownProps {
  currentStatus: GuitarStatus;
  onSelect: (s: GuitarStatus) => void;
  onClose: () => void;
}

export default function StatusDropdown({ currentStatus, onSelect, onClose }: StatusDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 mt-1 w-36 bg-card border border-border rounded-xl shadow-modal z-30 py-1 scale-in"
      role="listbox"
      aria-label="Change guitar status"
    >
      {statusOptions.map((opt) => (
        <button
          key={`status-dd-${opt.value}`}
          role="option"
          aria-selected={currentStatus === opt.value}
          onClick={() => onSelect(opt.value)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted transition-colors"
        >
          <Badge variant={opt.variant}>{opt.label}</Badge>
          {currentStatus === opt.value && <Check size={12} className="text-primary" />}
        </button>
      ))}
    </div>
  );
}